import { createContext, useContext, useEffect, useState, useRef } from 'react';
import supabase, { checkSession, refreshSession } from '../supabaseClient';
import { addCsrfHeader } from '../utils/csrfUtils';

const AuthContext = createContext();

// Global ref to track if auth has been initialized (persists across re-mounts)
const globalAuthInitialized = { current: false };
let globalSubscription = null;

// Track processed auth events to prevent duplicates
const processedAuthEvents = new Set();

// Track when we explicitly skip a refresh to ignore spurious Supabase events
let lastSkippedRefreshTime = null;

export const AuthContextProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [pgUserId, setPgUserId] = useState(null);
    const [pgUser, setPgUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [pgUserLastFetch, setPgUserLastFetch] = useState(null);
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
    const SESSION_REFRESH_INTERVAL = Math.max(60000, parseInt(process.env.REACT_APP_SESSION_REFRESH_INTERVAL || '300000', 10) || 300000); // min 60s, default 5 min
    
    // Use a ref to track if we're already fetching PG user data
    const isFetchingPgUser = useRef(false);
    
    // Helper to check if cache is valid
    const isPgUserCacheValid = () => {
        if (!pgUserLastFetch || !pgUser) return false;
        return (Date.now() - pgUserLastFetch) < CACHE_TTL;
    };

    // Function to fetch PostgreSQL user ID
    const fetchPgUserId = async (supabaseUser) => {
        console.log('AuthContext: fetchPgUserId called for user:', supabaseUser.id);
        
        // Return cached data if valid
        if (isPgUserCacheValid()) {
            console.log('AuthContext: Using cached PG user data');
            return pgUser;
        }
        
        // Prevent multiple simultaneous calls
        if (isFetchingPgUser.current) {
            console.log('AuthContext: Already fetching PG user, skipping duplicate call');
            return;
        }
        
        isFetchingPgUser.current = true;
        
        try {
            // Get the current session for the auth token
            // console.log('AuthContext: Getting current session for auth token');

            // Add timeout to prevent hanging in fetchPgUserId
            const sessionPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Session fetch timeout in fetchPgUserId')), 2000)
            );

            let result, sessionError;

            try {
                result = await Promise.race([sessionPromise, timeoutPromise]);
                sessionError = result.error;
            } catch (timeoutError) {
                console.error('AuthContext: Session fetch timeout in fetchPgUserId:', timeoutError.message);
                return; // Early return on timeout
            }

            const { data } = result;
            const { session } = data;

            // console.log('AuthContext: Session retrieved:', {
            //     hasToken: !!session?.access_token,
            //     sessionFetchTime: sessionEndTime - sessionStartTime,
            //     sessionError: sessionError?.message,
            //     userId: session?.user?.id
            // });

            if (sessionError) {
                console.error('AuthContext: Session error in fetchPgUserId:', sessionError);
                return;
            }
            
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Add auth header if we have a session
            if (session?.access_token) {
                headers['Authorization'] = `Bearer ${session.access_token}`;
                console.log('AuthContext: Added auth header for API request');
            } else {
                console.log('AuthContext: No access token available for auth header');
            }
            
            // First try to find user by supabase_id
            console.log(`AuthContext: Fetching PostgreSQL user by supabase_id: ${supabaseUser.id}`);
            const apiStartTime = Date.now();
            const response = await fetch(`/api/v1/users/supabase/${supabaseUser.id}`, {
                headers
            });
            const apiEndTime = Date.now();

            console.log('AuthContext: PostgreSQL user lookup response:', {
                status: response.status,
                ok: response.ok,
                apiCallTime: apiEndTime - apiStartTime,
                url: response.url
            });
            
            if (!response.ok) {
                console.error(`AuthContext: HTTP error from PostgreSQL user lookup: ${response.status}`);
                
                // Clear any previous auth errors
                setAuthError(null);

                if (!navigator.onLine) {
                    throw new Error('Network error: Please check your internet connection');
                }
                
                if (response.status === 401 || response.status === 403) {
                    throw new Error('Authentication error: Please try logging in again');
                }
                
                if (response.status === 500 || response.status === 502 || response.status === 503 || response.status === 504) {
                    throw new Error('Server error: Our services are temporarily unavailable. Please try again in a few minutes.');
                }
                
                throw new Error(`Unexpected error (${response.status}). Please try again.`);
            }
            
            console.log('about to log user data...')
            let userData = await response.json();
            console.log('AuthContext: PostgreSQL user lookup data:', userData);
            console.log('done logging user data')

            try {
                // If no user found by supabase_id during initial auth setup,
                // wait for webhook to create the user
                if (!userData || userData.length === 0) {
                    console.log('AuthContext: No user found, appears to be new signup. Waiting for webhook...');
                    const maxAttempts = 10;
                    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                        console.log(`AuthContext: Attempt ${attempt}/${maxAttempts} to find user`);
                        console.log(`AuthContext: Polling signup-check endpoint for user ${supabaseUser.id}`);
                        const checkResponse = await fetch(`/api/v1/users/supabase/${supabaseUser.id}/signup-check`);
                        console.log('AuthContext: signup-check response status:', checkResponse.status);
                        
                        if (checkResponse.ok) {
                            const checkData = await checkResponse.json();
                            console.log('AuthContext: signup-check response data:', checkData);
                            if (checkData && checkData.length > 0) {
                                console.log('AuthContext: User found after waiting');
                                userData = checkData;
                                break;
                            } else {
                                console.log('AuthContext: signup-check returned no user data');
                            }
                        } else {
                            console.error('AuthContext: signup-check request failed:', checkResponse.status);
                        }
                        
                        if (attempt < maxAttempts) {
                            console.log('AuthContext: User not found yet, waiting 1 second...');
                            await new Promise(resolve => setTimeout(resolve, 1000));
                        }
                    }
                }
            } catch (pollError) {
                console.error('AuthContext: Error while polling for user:', pollError);
                // Continue with existing data, let outer try-catch handle overall failure
            }

            if (userData && userData.length > 0) {
                // Update state with the user data
                const pgUserData = userData[0];
                console.log('AuthContext: Setting pgUserId:', pgUserData.id);
                setPgUserId(pgUserData.id);
                console.log('AuthContext: Setting pgUser:', pgUserData);
                setPgUser(pgUserData);
                setPgUserLastFetch(Date.now()); // Update cache timestamp
                return pgUserData; // Return the user data
            } else {
                console.log('AuthContext: No PostgreSQL user found, clearing pgUserId and pgUser');
                setPgUserId(null);
                setPgUser(null);
                return null;
            }
        } catch (error) {
            console.error("AuthContext: Error in fetchPgUserId:", error);
            setPgUserId(null);
            setPgUser(null);
            
            // Set appropriate error type
            const errorMessage = error.message || 'An unexpected error occurred';
            setAuthError({
                type: errorMessage.toLowerCase().includes('network error') ? 'network' :
                      errorMessage.toLowerCase().includes('server error') ? 'server' :
                      errorMessage.toLowerCase().includes('authentication error') ? 'auth' :
                      'unknown',
                message: errorMessage
            });
            
            return null;
        } finally {
            isFetchingPgUser.current = false; // Reset the fetching flag
            console.log('AuthContext: Completed fetchPgUserId, reset fetching flag');
        }
    };

    // Check for session and set up auth state listener
    // Add visibility change handler
        useEffect(() => {
        // Set up periodic session check
        console.log('AuthContext: Setting up periodic session check every', SESSION_REFRESH_INTERVAL, 'milliseconds');
        const sessionCheckInterval = setInterval(async () => {
            if (document.visibilityState === 'visible') {
                console.log('AuthContext: Performing periodic session check');
                const refreshResult = await refreshSession();
                if (refreshResult.error) {
                    // Don't reload - causes infinite loop on mobile when unauthenticated (no session to refresh).
                    // Reloading also doesn't help with network errors. Log and continue.
                    console.log('AuthContext: Periodic session refresh failed (no reload)', { error: refreshResult.error?.message });
                }
            }
        }, SESSION_REFRESH_INTERVAL); // Check every SESSION_REFRESH_INTERVAL milliseconds

        const handleVisibilityChange = async () => {
        if (document.visibilityState === 'visible') {
            console.log('AuthContext: Page became visible, checking if refresh needed');
            
            try {
                // Check how long we were hidden
                const hiddenDuration = Date.now() - (window.lastHiddenTime || 0);
                const LONG_INACTIVITY = 5 * 60 * 1000; // 5 minutes
                
                // Get current session to check expiry
                const { data } = await supabase.auth.getSession();
                
                if (data?.session) {
                    const timeUntilExpiry = (data.session.expires_at * 1000) - Date.now();
                    const EXPIRY_THRESHOLD = 5 * 60 * 1000; // 5 minutes
                    
                    // Only refresh if close to expiry OR long inactivity
                    const shouldRefresh = timeUntilExpiry < EXPIRY_THRESHOLD || hiddenDuration > LONG_INACTIVITY;
                    
                    if (shouldRefresh) {
                        console.log('AuthContext: Refreshing session', {
                            reason: timeUntilExpiry < EXPIRY_THRESHOLD ? 'expiring' : 'long inactivity',
                            timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + 's',
                            hiddenDuration: Math.round(hiddenDuration / 1000) + 's'
                        });
                        
                        const refreshResult = await refreshSession();
                        if (refreshResult.error) {
                            console.log('AuthContext: Session refresh failed');
                            const { session, error } = await checkSession();
                            if (error || !session) {
                                window.location.reload();
                                return;
                            }
                        }
                        // Let TOKEN_REFRESHED event handle state updates
                    } else {
                        console.log('AuthContext: Session still valid, skipping refresh', {
                            timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + 's',
                            hiddenDuration: Math.round(hiddenDuration / 1000) + 's'
                        });
                        // Mark that we explicitly skipped a refresh
                        lastSkippedRefreshTime = Date.now();
                    }
                }
            } catch (error) {
                console.error('AuthContext: Error handling visibility change:', error);
            }
        } else {
            // Track when page was hidden
            window.lastHiddenTime = Date.now();
        }
    };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(sessionCheckInterval);
            // Clear processed events on app shutdown
            processedAuthEvents.clear();
        };
    }, []);

    useEffect(() => {
        console.log('AuthContext: useEffect for session check running');
        // If already initialized globally, just get the current session
        if (globalAuthInitialized.current) {
            console.log('AuthContext: Auth already initialized globally, getting current session');
            const getCurrentSession = async () => {
                try {
                    // Try to refresh the session first
                    const refreshResult = await refreshSession();
                    if (refreshResult.error) {
                        console.log('AuthContext: Session refresh failed, getting current session with timeout', { error: refreshResult.error });

                        // Add timeout to prevent hanging
                        const sessionPromise = supabase.auth.getSession();
                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('Session fetch timeout')), 2000)
                        );

                        try {
                            const result = await Promise.race([sessionPromise, timeoutPromise]);
                            const { data } = result;
                            // console.log('AuthContext: Got current session, user exists:', !!data.session?.user, { sessionData: data });
                            setSession(data.session);
                            setUser(data.session?.user || null);
                            // console.log('AuthContext: State updated after getting current session', { userSet: !!data.session?.user, sessionSet: !!data.session });

                            if (data.session?.user) {
                                await fetchPgUserId(data.session.user);
                            }
                        } catch (timeoutError) {
                            console.error('AuthContext: Session fetch timeout in getCurrentSession:', timeoutError.message);

                            // Try to use the session from the event as fallback
                            // console.log('AuthContext: Attempting to use session from event as fallback in getCurrentSession');
                            const eventSession = session;
                            if (eventSession?.user) {
                                // console.log('AuthContext: Using session from event in getCurrentSession');
                                setSession(eventSession);
                                setUser(eventSession.user);
                                // console.log('AuthContext: State updated from event session fallback in getCurrentSession', {
                                //     userSet: !!eventSession?.user,
                                //     sessionSet: !!eventSession,
                                //     userId: eventSession?.user?.id,
                                //     emailConfirmed: eventSession?.user?.email_confirmed_at
                                // });

                                // Also fetch PG user data for fallback
                                if (eventSession.user) {
                                    // console.log('AuthContext: Scheduling PG user data fetch for event session fallback in getCurrentSession');
                                    fetchPgUserId(eventSession.user).catch(error => {
                                        console.error('AuthContext: Error fetching PG user data for fallback in getCurrentSession:', error);
                                    });
                                }
                            }
                        }
                    } else {
                        console.log('AuthContext: Session refreshed successfully', { userId: refreshResult.data.session?.user?.id, emailConfirmed: refreshResult.data.session?.user?.email_confirmed_at });
                        setSession(refreshResult.data.session);
                        setUser(refreshResult.data.session?.user || null);
                        console.log('AuthContext: State updated after session refresh', { userSet: !!refreshResult.data.session?.user, sessionSet: !!refreshResult.data.session });
                        
                        if (refreshResult.data.session?.user) {
                            await fetchPgUserId(refreshResult.data.session.user);
                        }
                    }
                } catch (error) {
                    console.error('Error getting current session:', error.message, { errorDetails: error });
                } finally {
                    console.log('AuthContext: Setting loading to false after session check');
                    setLoading(false);
                }
            };
            getCurrentSession();
            return;
        }
        
        // Mark as globally initialized
        console.log('AuthContext: First initialization, setting up auth');
        globalAuthInitialized.current = true;
        
        let isActive = true;
        
        const getInitialSession = async () => {
            console.log('AuthContext: Getting initial session');
            setLoading(true);
            
            try {
                const { data } = await supabase.auth.getSession();
                console.log('AuthContext: Initial session retrieved, user exists:', !!data.session?.user, { sessionData: data });
                
                if (!isActive) return; // Don't update state if component unmounted
                
                setSession(data.session);
                setUser(data.session?.user || null);
                console.log('AuthContext: State updated after initial session retrieval', { userSet: !!data.session?.user, sessionSet: !!data.session });
                
                // Pass the entire user object
                if (data.session?.user) {
                    await fetchPgUserId(data.session.user);
                }
            } catch (error) {
                console.error('Error getting initial session:', error.message, { errorDetails: error });
            } finally {
                if (isActive) {
                    console.log('AuthContext: Initial setup complete, setting loading to false');
                    setLoading(false);
                }
            }
        };
        
        getInitialSession();
        
        // Only set up the auth listener if we don't already have one
        if (!globalSubscription) {
            globalSubscription = supabase.auth.onAuthStateChange(
                async (_event, session) => {
                    // console.log('AuthContext: Auth state changed. Event:', _event, 'Session:', session?.user?.id, { sessionData: session });

                    // Prevent duplicate processing of the same auth event
                    const eventKey = `${_event}-${session?.user?.id}-${Date.now()}`;
                    if (processedAuthEvents.has(eventKey)) {
                        // console.log('AuthContext: Skipping duplicate auth event:', eventKey);
                        return;
                    }
                    processedAuthEvents.add(eventKey);

                    // console.log('AuthContext: User email confirmation status:', session?.user?.email_confirmed_at);
                    if (!isActive) return; // Don't update state if component unmounted

                    // Check if this is a spurious event
                    // 1. Same user and we already have data, OR
                    // 2. Event happens within 3 seconds of explicitly skipping a refresh
                    const timeSinceSkip = lastSkippedRefreshTime ? Date.now() - lastSkippedRefreshTime : Infinity;
                    const isSpuriousEvent = (
                        (_event === 'SIGNED_IN' || _event === 'USER_UPDATED') && 
                        (
                            (session?.user?.id === user?.id && pgUser !== null) ||
                            (timeSinceSkip < 3000) // Within 3 seconds of skipping refresh
                        )
                    );

                    // Handle token refresh silently — the user hasn't changed,
                    // only the auth tokens were renewed. Updating session state
                    // without the loading cycle avoids a visible "refresh" flash.
                    if (_event === 'TOKEN_REFRESHED') {
                        console.log('AuthContext: Token refreshed, updating session silently');
                        setSession(session);
                        setUser(session?.user || null);
                        return;
                    }

                    // Don't disrupt UI for spurious events when user data is already loaded
                    if (!isSpuriousEvent) {
                        setLoading(true); // Set loading while we process auth change
                    } else {
                        console.log('AuthContext: Spurious auth event detected, skipping reload', { 
                            event: _event, 
                            sameUser: session?.user?.id === user?.id,
                            hasPgUser: pgUser !== null,
                            timeSinceSkipMs: timeSinceSkip !== Infinity ? timeSinceSkip : 'N/A',
                            reason: timeSinceSkip < 3000 ? 'Recent skip' : 'Same user with data'
                        });
                    }
                    
                    try {
                        // Handle sign out event explicitly
                        if (_event === 'SIGNED_OUT') {
                            console.log('AuthContext: User signed out');
                            setSession(null);
                            setUser(null);
                            setPgUserId(null);
                            setPgUser(null);
                            console.log('AuthContext: State cleared after sign out');
                            return; // Exit early
                        }
                        
                        // Skip processing spurious events entirely
                        if (isSpuriousEvent) {
                            console.log('AuthContext: Skipping spurious event processing');
                            return; // Exit early without any updates
                        }
                        
                        if (_event === 'SIGNED_IN' || _event === 'USER_UPDATED') {
                            console.log('AuthContext: SIGNED_IN or USER_UPDATED event, fetching fresh session');

                            try {
                                // Basic connectivity check (keep this for production monitoring)
                                try {
                                    await fetch(`${supabase.supabaseUrl}/rest/v1/`, {
                                        method: 'HEAD',
                                        headers: {
                                            'apikey': supabase.supabaseKey
                                        }
                                    });
                                } catch (connectivityError) {
                                    console.warn('AuthContext: Supabase connectivity check failed:', connectivityError.message);
                                }

                                // Force a re-fetch of the session to ensure we have the latest data
                                const sessionPromise = supabase.auth.getSession();
                                const timeoutPromise = new Promise((_, reject) =>
                                    setTimeout(() => reject(new Error('Session fetch timeout')), 2000)
                                );

                                let data, error;
                                try {
                                    const result = await Promise.race([sessionPromise, timeoutPromise]);
                                    data = result.data;
                                    error = result.error;
                                } catch (timeoutError) {
                                    console.error('AuthContext: Session fetch timeout:', timeoutError.message);
                                    error = timeoutError;
                                }

                                if (error) {
                                    console.error('AuthContext: Error fetching fresh session:', error);
                                    // Try to use the session from the event as fallback
                                    console.log('AuthContext: Attempting to use session from event as fallback');
                                    const eventSession = session;
                                    if (eventSession?.user) {
                                        console.log('AuthContext: Using session from event');
                                        setSession(eventSession);
                                        setUser(eventSession.user);
                                        console.log('AuthContext: State updated from event session fallback', {
                                            userSet: !!eventSession?.user,
                                            sessionSet: !!eventSession,
                                            userId: eventSession?.user?.id,
                                            emailConfirmed: eventSession?.user?.email_confirmed_at
                                        });

                                        // Also fetch PG user data for fallback
                                        if (eventSession.user) {
                                            console.log('AuthContext: Scheduling PG user data fetch for event session fallback');
                                            fetchPgUserId(eventSession.user).catch(error => {
                                                console.error('AuthContext: Error fetching PG user data for fallback:', error);
                                            });
                                        }
                                    }
                                    return;
                                }

                                const freshSession = data.session;
                                console.log('AuthContext: Fresh session fetched:', freshSession?.user?.id, {
                                    hasSession: !!freshSession,
                                    hasUser: !!freshSession?.user,
                                    emailConfirmed: freshSession?.user?.email_confirmed_at,
                                    userId: freshSession?.user?.id
                                });

                                setSession(freshSession);
                                setUser(freshSession?.user || null);
                                console.log('AuthContext: State updated after fresh session fetch', {
                                    userSet: !!freshSession?.user,
                                    sessionSet: !!freshSession,
                                    userId: freshSession?.user?.id,
                                    emailConfirmed: freshSession?.user?.email_confirmed_at
                                });

                                if (freshSession?.user) {
                                    console.log('AuthContext: Scheduling PG user data fetch for fresh session', { userId: freshSession.user.id, emailConfirmed: freshSession.user.email_confirmed_at });

                                    // Don't await - let this happen asynchronously so it doesn't block state updates
                                    fetchPgUserId(freshSession.user).catch(error => {
                                        console.error('AuthContext: Error fetching PG user data:', error);
                                    });

                                    // Check if email was just verified
                                    if (freshSession.user.email_confirmed_at && _event === 'USER_UPDATED') {
                                        console.log('AuthContext: Email verification detected, updating PG user');
                                        // Don't await this either
                                        updateEmailConfirmationSecure(freshSession.user.id).catch(error => {
                                            console.error('Failed to update email confirmation:', error.message, { errorDetails: error });
                                        });
                                    }
                                }
                            } catch (sessionError) {
                                console.error('AuthContext: Exception during fresh session fetch:', sessionError);
                            }

                            // Prevent duplicate auth state change processing
                            console.log('AuthContext: Auth state change processing complete for event:', _event);
                        } else {
                            console.log('AuthContext: Setting session and user from event', { event: _event });
                            setSession(session);
                            setUser(session?.user || null);
                            console.log('AuthContext: State updated from event', { userSet: !!session?.user, sessionSet: !!session });
                            
                            if (session?.user) {
                                console.log('AuthContext: User exists in session, fetching PG user', { userId: session.user.id });
                                await fetchPgUserId(session.user);
                            } else {
                                console.log('AuthContext: No user in session, clearing PG user data');
                                setPgUserId(null);
                                setPgUser(null);
                            }
                        }
                    } catch (error) {
                        console.error('AuthContext: Error handling auth state change:', error.message, { errorDetails: error });
                    } finally {
                        // Only set loading false if we set it to true (not for spurious events)
                        if (!isSpuriousEvent) {
                            console.log('AuthContext: Auth state change complete, setting loading false');
                            setLoading(false);
                        }
                    }
                }
            );
        }
        
        return () => {
            isActive = false; // Mark component as unmounted
            // Don't unsubscribe from global subscription here
        };
    }, []);

    // Cleanup effect for app shutdown
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (globalSubscription) {
                globalSubscription.data.subscription.unsubscribe();
                globalSubscription = null;
            }
            globalAuthInitialized.current = false;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // signup function
    const signUpNewUser = async (email, password, firstName, lastName) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: { firstName, lastName },
            },
        });

        if (error) {
            return { success: false, error: error.message };
        }
        
        // Wait for PostgreSQL user to be created via webhook
        const pgUserCreated = await waitForPgUserCreation(data.user.id, email);
        
        if (!pgUserCreated) {
            // If PostgreSQL user creation failed, we should clean up the Supabase user
            console.error('PostgreSQL user creation failed, cleaning up Supabase user');
            // You could add cleanup logic here if needed
            return { 
                success: false, 
                error: 'Error creating account. Please try again or contact support.' 
            };
        }
        
        return { success: true, data: data };
    }

    // Helper function to wait for PostgreSQL user creation
    const waitForPgUserCreation = async (supabaseId, email, maxAttempts = 10) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                // Try to find the PostgreSQL user using the unauthenticated signup-check endpoint
                const response = await fetch(`/api/v1/users/supabase/${supabaseId}/signup-check`);

                console.log('waitForPgUserCreation response: ', response);
                
                if (response.ok) {
                    console.log('Response ok')
                    const data = await response.json();
                    if (data && data.length > 0) {
                        console.log(`PostgreSQL user found on attempt ${attempt}`);
                        return true;
                    } else {
                        console.log('User not found yet. Response data:', JSON.stringify(data));
                    }
                } else {
                    console.log('waitForPgUserCreation response not ok. ' + JSON.stringify(response));
                }
                
                console.log(`Attempt ${attempt}/${maxAttempts}: PostgreSQL user not found yet, waiting...`);
                
                // Wait 1 second before next attempt
                await new Promise(resolve => setTimeout(resolve, 1000));
                
            } catch (error) {
                console.error('Error checking for PostgreSQL user:', error);
            }
        }
        
        console.error('PostgreSQL user not found after', maxAttempts, 'attempts');
        return false;
    }

    // sign in function
    const signInUser = async (email, password) => {
        console.log('AuthContext: signInUser called for', email);
        try {
            console.log('AuthContext: Calling supabase.auth.signInWithPassword...');
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (error) {
                console.error('AuthContext: Sign-in error from Supabase:', error);
                return { success: false, error: error.message };
            }
            
            console.log('AuthContext: Sign-in successful, user data:', data.user.id);
            
            // Immediately update the user state
            setUser(data.user);
            setSession(data.session);
            
            // Start fetching the PG user data
            if (data.user) {
                await fetchPgUserId(data.user);
            }
            
            return { success: true, data: data };
        } catch (error) {
            console.error('AuthContext: Exception during sign-in:', error);
            return { success: false, error: error.message };
        }
    };

    // sign in with Google
    const signInWithGoogle = async () => {
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin + '/auth-loading'  // Redirect to a loading page instead
                }
            });
            
            if (error) {
                return { success: false, error: error.message };
            }
            return { success: true, data: data };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // sign out function
    const signOutUser = async () => {
        setLoading(true);
        try {
            const { session, error: sessionError } = await checkSession();
            
            if (session) {
                try {
                    const { error } = await supabase.auth.signOut();
                    if (error) {
                        console.error("Error signing out from Supabase:", error);
                    }
                } catch (signOutError) {
                    console.error("Exception during Supabase sign out:", signOutError);
                }
            }
            
            // Clear all auth state regardless of API success
            const clearAuthState = () => {
                setSession(null);
                setUser(null);
                setPgUserId(null);
                setPgUser(null);
            };
            
            // Execute the state clearing function
            clearAuthState();
            
            // Reset global auth state
            globalAuthInitialized.current = false;
            if (globalSubscription) {
                globalSubscription.data.subscription.unsubscribe();
                globalSubscription = null;
            }
            
            // Force a re-render
            setLoading(false);
            
            // Force a page reload as a last resort
            window.location.href = '/';
            
            return { success: true };
        } catch (error) {
            console.error("Error in signOutUser function:", error);
            setLoading(false);
            return { success: false, error: error.message };
        }
    };

    // Secure email confirmation function
    const updateEmailConfirmationSecure = async (supabaseUserId) => {
        try {
            // Get the current session for the auth token with timeout
            const sessionPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Session fetch timeout in updateEmailConfirmationSecure')), 2000)
            );

            let result;
            try {
                result = await Promise.race([sessionPromise, timeoutPromise]);
            } catch (timeoutError) {
                console.error('AuthContext: Session fetch timeout in updateEmailConfirmationSecure:', timeoutError.message);
                throw new Error('Failed to get session for email confirmation');
            }

            const { data } = result;
            const { session: currentSession } = data;

            if (!currentSession?.access_token) {
                throw new Error('No valid session found');
            }
            
            // Add CSRF token to headers along with auth token
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentSession.access_token}`
            });
            
            // Call secure backend endpoint
            const response = await fetch('/api/v1/users/confirm-email-secure', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    supabase_id: supabaseUserId
                }),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to update email confirmation: ${response.status}`);
            }
            
            const responseData = await response.json();

            // Refresh the pgUser state to reflect the updated confirmation status
            if (responseData.user) {
                setPgUser(responseData.user);
            }

            return responseData;
        } catch (error) {
            console.error("Error updating email confirmation:", error);
            throw error;
        }
    };

    // Function to force refresh PG user data
    const refreshPgUser = async () => {
        if (!user) return null;
        setPgUserLastFetch(null); // Invalidate cache
        return fetchPgUserId(user);
    };

    // Function to update a specific property of pgUser without refetching
    const updatePgUserProperty = (property, value) => {
        if (!pgUser) return;
        setPgUser(prevUser => ({
            ...prevUser,
            [property]: value
        }));
    };

    const value = {
        user,
        pgUserId,
        pgUser,
        loading,
        signUpNewUser,
        signOutUser,
        signInUser,
        signInWithGoogle,
        updateEmailConfirmationSecure,
        sessionToken: session?.access_token || null,
        authError,
        clearAuthError: () => setAuthError(null),
        refreshPgUser, // Add refresh function to context
        updatePgUserProperty // Add property update function to context
    };

    return (
        <AuthContext.Provider value={value}>
            {console.log('AuthContext: Rendering provider, loading state:', loading)}
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
